import { CenteredFloatingActions } from "@/components/floating-actions/FloatingActions";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { useState } from "react";
import { NoteObservable } from "@/store/note/NoteObservable";
import { MdsIconButton } from "@/design-system/components/icon-button";
import { toastModule } from "@/modules/toast";
import { css } from "@/domains/emotion";
import { mdsColors } from "@/design-system/foundations";

interface NoteSuggestionRefineActionsProps {
  noteObservable: NoteObservable;
  setShowRefineInput: (show: boolean) => void;
  verticalOffset?: number;
}

export const NoteSuggestionRefineActions = observer(function NoteSuggestionRefineActions({
  noteObservable,
  setShowRefineInput,
  verticalOffset,
}: NoteSuggestionRefineActionsProps) {
  const [refineText, setRefineText] = useState("");
  const trimmedRefineText = refineText.trim();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!trimmedRefineText || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await noteObservable.refineNoteSuggestion(trimmedRefineText);
      setShowRefineInput(false);
    } catch {
      toastModule.triggerToast({ content: "Failed to refine note. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setShowRefineInput(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    }
  };

  return (
    <CenteredFloatingActions isOpen={true} verticalOffset={verticalOffset}>
      <StyledContainer>
        <MdsIcon kind={MdsIconKind.Sparkle} />
        <StyledText>Refine Note</StyledText>
        <StyledInput
          value={refineText}
          onChange={e => setRefineText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Describe how you want to refine the edits."
          disabled={isSubmitting}
          autoFocus
        />
        <MdsIconButton
          iconKind={MdsIconKind.Send}
          onClick={handleSubmit}
          isDisabled={isSubmitting}
          isLoading={isSubmitting}
          iconStyles={submitIconButtonStyles}
        />
        <MdsIconButton iconKind={MdsIconKind.Exit} onClick={handleCancel} />
      </StyledContainer>
    </CenteredFloatingActions>
  );
});

const StyledContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grey.x25};
  border-radius: ${({ theme }) => theme.borderRadius.mediumLarge}px;
  box-shadow: 0px 6px 10px rgba(69, 78, 104, 0.2);
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  gap: ${({ theme }) => theme.spacing.sm};
  width: 95vw;
  max-width: 600px;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const StyledText = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  color: ${({ theme }) => theme.colors.grey.x600};
  margin-right: ${({ theme }) => theme.spacing.sm};
`;

const StyledInput = styled.input`
  border: none;
  flex: 1;
  color: ${({ theme }) => theme.colors.grey.x600};
  &::placeholder {
    color: ${({ theme }) => theme.colors.blue.x300};
  }
  &:disabled {
    background-color: transparent;
  }
`;

const submitIconButtonStyles = css({
  color: mdsColors().mblue.x600,
});
